import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    X, CreditCard, User, Phone, Mail, DollarSign,
    CheckCircle, Copy, MessageCircle, Loader2, ChevronRight,
    ArrowRight, RefreshCw, Zap, Wallet, Info, Coins
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// ===== CRYPTO WALLET VISUAL =====
const CryptoWalletDisplay = ({ address = "", amount = 0 }) => (
    <div className="relative w-full max-w-[300px] mx-auto select-none">
        <div className="absolute inset-0 rounded-2xl blur-2xl opacity-60 translate-y-6 scale-90"
            style={{ background: "linear-gradient(135deg, #26A17B 0%, #000000 100%)" }} />
        <div className="relative rounded-2xl overflow-hidden text-white border border-white/20"
            style={{
                background: "linear-gradient(145deg, #1a1a1a 0%, #26A17B 100%)",
                boxShadow: "0 20px 60px rgba(38,161,123,0.3)"
            }}>
            <div className="relative p-5">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[3px] text-green-300">TRON NETWORK</p>
                        <p className="text-lg font-black mt-0.5 tracking-wide flex items-center gap-2">
                            <Coins className="w-5 h-5" /> USDT TRC20
                        </p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                        <Zap className="w-5 h-5 text-green-400" />
                    </div>
                </div>

                <div className="mb-6">
                    <p className="text-[9px] text-gray-400 uppercase tracking-widest mb-1">Addresse Wallet Ou</p>
                    <p className="text-xs font-mono text-white break-all bg-black/20 p-2 rounded-lg border border-white/10">
                        {address || "T... (Antre addresse ou an ba)"}
                    </p>
                </div>

                <div className="flex justify-between items-end">
                    <div>
                        <p className="text-[9px] text-gray-400 uppercase tracking-wider">Kantite USD</p>
                        <p className="text-xl font-black text-white">${amount > 0 ? parseFloat(amount).toFixed(2) : "0.00"}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[9px] text-gray-400 uppercase">Estati</p>
                        <p className="text-xs font-bold text-green-400">TRC20</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

// ===== UI COMPONENTS (Form & Payment) =====
const CryptoStepForm = ({ onSubmit, serviceInfo, loading }) => {
    const [clientName, setClientName] = useState("");
    const [clientPhone, setClientPhone] = useState("");
    const [walletAddress, setWalletAddress] = useState("");
    const [amountUsd, setAmountUsd] = useState("");

    const rate = serviceInfo?.usd_to_htg_rate || 148;
    const feePct = serviceInfo?.service_fee_percent || 0.05;
    const amt = parseFloat(amountUsd) || 0;
    const totalUsd = parseFloat((amt * (1 + feePct)).toFixed(2));
    const totalHtg = Math.round(totalUsd * rate);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!clientName.trim()) return toast.error("Non ou obligatwa");
        if (!walletAddress.trim()) return toast.error("Mete addresse TRC20 ou a");
        if (amt < 10) return toast.error("Minimòm se $10 USD");

        onSubmit({
            client_name: clientName.trim(),
            client_phone: clientPhone.trim(),
            wallet_address: walletAddress.trim(),
            amount_usd: amt
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-5">
            <CryptoWalletDisplay address={walletAddress} amount={amt} />

            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <p className="text-xs text-blue-800 leading-relaxed font-medium">
                    Asire w addresse a se yon addresse **TRC20 (Tron Network)**. Si w bay move addresse, kòb la ka pèdi nèt.
                </p>
            </div>

            <div className="space-y-4">
                <div>
                    <Label className="text-xs font-bold text-gray-500 uppercase">Non Konplè Ou</Label>
                    <Input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Eg: Jean Dupont" className="rounded-xl mt-1" required />
                </div>
                <div>
                    <Label className="text-xs font-bold text-gray-500 uppercase">Addresse USDT TRC20 Ou</Label>
                    <Input value={walletAddress} onChange={e => setWalletAddress(e.target.value)} placeholder="T..." className="rounded-xl mt-1 font-mono text-sm" required />
                </div>
                <div>
                    <Label className="text-xs font-bold text-gray-500 uppercase">Kantite USDT ou vle</Label>
                    <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-gray-400">$</span>
                        <Input type="number" value={amountUsd} onChange={e => setAmountUsd(e.target.value)} className="pl-7 rounded-xl" placeholder="0.00" min="10" required />
                    </div>
                </div>

                {amt > 0 && (
                    <div className="bg-gray-50 p-3 rounded-xl space-y-2 border border-gray-100">
                        <div className="flex justify-between text-xs"><span>USDT</span><span className="font-bold">${amt.toFixed(2)}</span></div>
                        <div className="flex justify-between text-xs"><span>Frè ({(feePct * 100).toFixed(0)}%)</span><span className="font-bold">${(amt * feePct).toFixed(2)}</span></div>
                        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                            <span className="text-sm font-black">Ou Peye (HTG)</span>
                            <span className="text-lg font-black text-green-700">{totalHtg.toLocaleString()} HTG</span>
                        </div>
                    </div>
                )}
            </div>

            <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl font-black text-white bg-green-600 hover:bg-green-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                Soumèt Demann USDT
            </Button>
        </form>
    );
};

const CryptoStepPayment = ({ result, onClose }) => {
    const info = result?.payment_info;
    const [isVerifying, setIsVerifying] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    const [status, setStatus] = useState("pending");

    const handlePay = async () => {
        setIsPaying(true);
        try {
            const { data } = await axios.post(`${API}/crypto/recharge/${result.request_id}/pay-moncash`);
            if (data.redirect_url) window.location.href = data.redirect_url;
        } catch (e) { toast.error("Erè MonCash"); } finally { setIsPaying(false); }
    };

    const verify = async () => {
        setIsVerifying(true);
        try {
            const { data } = await axios.get(`${API}/crypto/recharge/${result.request_id}/verify-payment`);
            if (data.success) {
                setStatus(data.status);
                toast.success("Peman resevwa!");
            } else toast.error("Peman poko fèt");
        } catch (e) { toast.error("Erè"); } finally { setIsVerifying(false); }
    };

    if (status === "payment_received") {
        return (
            <div className="text-center py-6 space-y-4">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
                <h3 className="text-xl font-black">Peman Konfime!</h3>
                <p className="text-sm text-gray-500">Nou ap voye USDT a sou wallet ou kounye a. Sa ka pran 15-30 minit.</p>
                <Button onClick={onClose} className="w-full rounded-xl">Fèmen</Button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="bg-green-600 p-5 rounded-2xl text-center text-white">
                <p className="text-sm opacity-80">Demann Kreye</p>
                <p className="text-lg font-black">{result.request_number}</p>
            </div>

            <Button onClick={handlePay} disabled={isPaying} className="w-full h-14 rounded-2xl bg-[#D10A3B] hover:bg-[#9E082D] text-white font-black text-lg shadow-xl flex gap-3">
                {isPaying ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Zap className="w-6 h-6" /> Peye ak MonCash Portal</>}
            </Button>

            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-200">
                <p className="text-xs font-bold text-gray-400 uppercase mb-3 text-center">Oswa Transefè Manyèl</p>
                <div className="space-y-2">
                    <div className="flex justify-between items-center bg-white p-2.5 rounded-xl border border-gray-200">
                        <div><p className="text-[9px] text-gray-400 font-bold uppercase">Nimewo MonCash</p><p className="text-sm font-black">{info.moncash_number}</p></div>
                    </div>
                    <div className="flex justify-between items-center bg-white p-2.5 rounded-xl border border-gray-200">
                        <div><p className="text-[9px] text-gray-400 font-bold uppercase">Kantite HTG</p><p className="text-sm font-black text-red-600">{info.amount_htg.toLocaleString()} HTG</p></div>
                    </div>
                </div>
                <Button variant="ghost" onClick={verify} disabled={isVerifying} className="w-full mt-3 text-xs text-gray-500">
                    {isVerifying ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <RefreshCw className="w-3 h-3 mr-2" />} Tcheke Estati
                </Button>
            </div>

            <Button onClick={onClose} variant="secondary" className="w-full rounded-xl">Fèmen</Button>
        </div>
    );
};

// ===== MAIN MODAL =====
export const CryptoRechargeModal = ({ isOpen, onClose }) => {
    const [step, setStep] = useState(1);
    const [result, setResult] = useState(null);
    const [serviceInfo, setServiceInfo] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setStep(1);
            axios.get(`${API}/crypto/info`).then(r => setServiceInfo(r.data)).catch(console.error);
        }
    }, [isOpen]);

    const submitRequest = async (formData) => {
        setLoading(true);
        const token = localStorage.getItem("buyer_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        try {
            const { data } = await axios.post(`${API}/crypto/recharge/request`, formData, { headers });
            setResult(data);
            setStep(2);
        } catch (e) { toast.error("Erè nan soumèt"); } finally { setLoading(false); }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="relative w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
                    <div className="p-5 flex items-center justify-between text-white" style={{ background: "linear-gradient(135deg, #111 0%, #26A17B 100%)" }}>
                        <div className="flex items-center gap-3">
                            <Coins className="w-6 h-6 text-green-400" />
                            <div><h2 className="font-black text-base">Achte USDT TRC20</h2><p className="text-[10px] opacity-70">Enstantane sou wallet ou</p></div>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl"><X className="w-4 h-4" /></button>
                    </div>

                    <div className="p-5 max-h-[85vh] overflow-y-auto">
                        {step === 1 ? <CryptoStepForm onSubmit={submitRequest} serviceInfo={serviceInfo} loading={loading} /> : <CryptoStepPayment result={result} onClose={onClose} />}
                    </div>
                </div>
            </div>
        </>
    );
};

